/** @format */

import React, { useCallback, useContext } from "react";
import {
  Button,
  Container,
  Dropdown,
  Icon,
  Image,
  Flag,
} from "semantic-ui-react";
import { logoutUser, updateSideBarState } from "../../store/actions";
import "./index.scss";
import { Link, useLocation } from "@reach/router";
import qs from "qs";
import { useDispatch, useSelector } from "react-redux";
import { isAllowedToRead } from "../../helper/permissions";
import { useTranslation } from "react-i18next";
import { AxiosContext } from "../../context/AxiosContext";
import awsconfig from "../../config/awsconfig";
import { useWindowSize } from "../../hooks/use-window-size";
import { SUPPORTED_LANGUAGES } from "../../i18n";
import Breadcrumbs from "./Breadcrumbs";
import NavigationIcon from "./swobbee-icon.png";

const NAV_GROUPS = [
  {
    labelKey: "Operations",
    items: [
      {
        permission: "SharingPoint",
        href: "/sharingpoints",
        label: "mainsidebar.sharingpoints.label",
        icon: "lightning",
      },
      {
        permission: "SharingPoint",
        href: "/",
        label: "mainsidebar.systemSummary.label",
        icon: "pie chart",
      },
      {
        permission: "SharingPoint",
        href: "/swaps",
        label: "mainsidebar.statistics.label",
        icon: "chart bar outline",
        children: [
          {
            href: "/swaps",
            tab: "swapTable",
            label: "Swap table",
            icon: "sync",
          },
          {
            href: "/swaps",
            tab: "chart",
            label: "Chart",
            icon: "chart bar outline",
          },
          {
            href: "/swaps",
            tab: "service-ratings",
            label: "Service ratings",
            icon: "star",
          },
          {
            href: "/swaps",
            tab: "station-usage",
            label: "Station usage",
            icon: "location arrow",
          },
          {
            href: "/error-history",
            label: "mainsidebar.errorHistory.label",
            icon: "warning sign",
            permission: "ErrorHistory",
          },
        ],
      },
      {
        permission: "NewBattery",
        href: "/batteries",
        label: "mainsidebar.batteries.label",
        icon: "battery full",
        children: [
          {
            href: "/batteries",
            tab: "list",
            label: "mainsidebar.batteries.label",
            icon: "list",
          },
          {
            href: "/batteries",
            tab: "chargeHistory",
            label: "mainsidebar.batteries.chargeHistory",
            icon: "history",
          },
          {
            href: "/batteries",
            tab: "interaction-log",
            label: "mainsidebar.batteries.interactionLog",
            icon: "unlock alternate",
          },
        ],
      },
    ],
  },
  {
    labelKey: "Admin & support",
    items: [
      {
        permission: "ErrorHistory",
        href: "/locations",
        label: "mainsidebar.locations.label",
        icon: "point",
      },
      {
        permission: "ErrorHistory",
        href: "/error-history",
        label: "Error History",
        icon: "warning sign",
      },
      {
        permission: "SharingPoint",
        href: "/users",
        label: "User-Management",
        icon: "user plus",
        children: [
          { href: "/users", tab: "users", label: "Users", icon: "users" },
          {
            href: "/users",
            tab: "battery-assignments",
            label: "Battery assignments",
            icon: "battery full",
          },
          { href: "/users", tab: "groups", label: "Groups", icon: "group" },
          {
            href: "/users",
            tab: "notification-tasks",
            label: "Notification Tasks",
            icon: "bell",
          },
        ],
      },
      {
        permission: "ErrorHistory",
        href: "/release-notes",
        label: "mainsidebar.releaseNotes.label",
        icon: "newspaper outline",
      },
    ],
  },
];

const NavItem = ({ item, collapsed, isActive, onNavigate }) => {
  const { t } = useTranslation();

  if (!isAllowedToRead(item.permission)) return null;

  const label = item.label.startsWith("mainsidebar.")
    ? t(item.label)
    : item.label;

  return (
    <li className="new-layout-nav-item">
      <Link
        to={item.href}
        onClick={onNavigate}
        className={`new-layout-nav-link ${isActive ? "new-layout-nav-link--active" : ""}`}
        title={collapsed ? label : undefined}
      >
        <span className="new-layout-nav-icon">
          <Icon name={item.icon} />
        </span>
        {!collapsed && <span className="new-layout-nav-label">{label}</span>}
      </Link>
    </li>
  );
};

const NavItemWithChildren = ({
  item,
  collapsed,
  isParentActive,
  isChildActive,
  onNavigate,
}) => {
  const { t } = useTranslation();

  if (!isAllowedToRead(item.permission)) return null;

  const label = item.label.startsWith("mainsidebar.")
    ? t(item.label)
    : item.label;
  const showChildren = !collapsed && item.children?.length && isParentActive;

  return (
    <li className="new-layout-nav-item new-layout-nav-item--has-children">
      <Link
        to={item.href}
        onClick={onNavigate}
        className={`new-layout-nav-link ${isParentActive ? "new-layout-nav-link--active" : ""}`}
        title={collapsed ? label : undefined}
      >
        <span className="new-layout-nav-icon">
          <Icon name={item.icon} />
        </span>
        {!collapsed && <span className="new-layout-nav-label">{label}</span>}
        {!collapsed && item.children?.length && (
          <Icon
            name={showChildren ? "chevron down" : "chevron right"}
            className="new-layout-nav-chevron"
          />
        )}
      </Link>
      {showChildren && (
        <ul className="new-layout-nav-list new-layout-nav-list--nested">
          {item.children
            .filter(
              (child) => !child.permission || isAllowedToRead(child.permission),
            )
            .map((child) => {
              const childLabel = child.label.startsWith("mainsidebar.")
                ? t(child.label)
                : child.label;
              const childHref = child.tab
                ? `${child.href}?tab=${child.tab}`
                : child.href;
              const active = isChildActive(child);
              return (
                <li
                  key={`${item.href}-${childHref}`}
                  className="new-layout-nav-item"
                >
                  <Link
                    to={childHref}
                    onClick={onNavigate}
                    className={`new-layout-nav-link new-layout-nav-link--nested ${active ? "new-layout-nav-link--active" : ""}`}
                    title={childLabel}
                  >
                    <span className="new-layout-nav-icon">
                      <Icon name={child.icon || item.icon} />
                    </span>
                    <span className="new-layout-nav-label">{childLabel}</span>
                  </Link>
                </li>
              );
            })}
        </ul>
      )}
    </li>
  );
};

const Sidebar = ({ collapsed, closeOnNavigate }) => {
  const { pathname, search } = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isActive = useCallback(
    (href) => {
      if (href === "/") {
        return (
          pathname === "/" || pathname === "" || pathname === "/system-summary"
        );
      }
      return pathname === href || pathname.startsWith(href + "/");
    },
    [pathname],
  );

  const currentUsersTab =
    pathname === "/users"
      ? qs.parse(search, { ignoreQueryPrefix: true }).tab || "users"
      : null;

  const isUsersChildActive = useCallback(
    (child) => {
      if (child.href === "/error-history" && !child.tab) {
        return pathname === "/error-history";
      }
      return (
        pathname === "/users" &&
        (currentUsersTab || "users") === (child.tab || "users")
      );
    },
    [pathname, currentUsersTab],
  );

  const currentSwapsTab =
    pathname === "/swaps"
      ? qs.parse(search, { ignoreQueryPrefix: true }).tab || "swapTable"
      : null;

  const isSwapsChildActive = useCallback(
    (child) => {
      if (child.href === "/error-history" && !child.tab) {
        return pathname === "/error-history";
      }
      return (
        pathname === "/swaps" &&
        (currentSwapsTab || "swapTable") === (child.tab || "swapTable")
      );
    },
    [pathname, currentSwapsTab],
  );

  const currentBatteriesTab =
    pathname === "/batteries"
      ? qs.parse(search, { ignoreQueryPrefix: true }).tab || "list"
      : null;

  const isBatteriesChildActive = useCallback(
    (child) => {
      if (!child.tab) {
        return pathname === child.href || pathname.startsWith(`${child.href}/`);
      }
      return (
        pathname === "/batteries" &&
        (currentBatteriesTab || "list") === child.tab
      );
    },
    [pathname, currentBatteriesTab],
  );

  const handleNavClick = useCallback(() => {
    if (closeOnNavigate) dispatch(updateSideBarState(true));
  }, [dispatch, closeOnNavigate]);

  return (
    <aside
      className={`new-layout-sidebar new-layout-sidebar--${collapsed ? "collapsed" : "expanded"}`}
    >
      <div className="new-layout-sidebar-inner">
        <div className="new-layout-sidebar-header">
          <Link
            to="/"
            className="new-layout-logo"
            onClick={collapsed ? undefined : handleNavClick}
          >
            <img
              src={
                collapsed
                  ? NavigationIcon
                  : "https://we-are-hiring.cdn.personio.de/logos/25749/default/e8a6675690ed898b3d859a1eb3f89cf2.png"
              }
              alt="Swobbee"
              width={collapsed ? 32 : 110}
              height="auto"
            />
          </Link>
        </div>
        <nav className="new-layout-nav">
          {NAV_GROUPS.map((group) => (
            <div key={group.labelKey} className="new-layout-nav-group">
              {!collapsed && (
                <div className="new-layout-nav-group-title">
                  {t(group.labelKey) || group.labelKey}
                </div>
              )}
              <ul className="new-layout-nav-list">
                {group.items.map((item) =>
                  item.children?.length ? (
                    <NavItemWithChildren
                      key={item.href}
                      item={item}
                      collapsed={collapsed}
                      isParentActive={isActive(item.href)}
                      isChildActive={
                        item.href === "/users"
                          ? isUsersChildActive
                          : item.href === "/swaps"
                            ? isSwapsChildActive
                            : item.href === "/batteries"
                              ? isBatteriesChildActive
                              : (child) =>
                                  isActive(
                                    child.tab
                                      ? `${child.href}?tab=${child.tab}`
                                      : child.href,
                                  )
                      }
                      onNavigate={handleNavClick}
                    />
                  ) : (
                    <NavItem
                      key={item.href}
                      item={item}
                      collapsed={collapsed}
                      isActive={isActive(item.href)}
                      onNavigate={handleNavClick}
                    />
                  ),
                )}
              </ul>
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );
};

const MainHeader = ({ session, lang, setLang, collapsed, compact }) => {
  const dispatch = useDispatch();
  const sidebarCollapsed = useSelector(
    (state) => state.appSettings.sidebarIsCollapsed,
  );

  const toggleSidebar = useCallback(() => {
    dispatch(updateSideBarState(!sidebarCollapsed));
  }, [dispatch, sidebarCollapsed]);

  return (
    <header
      className={`new-layout-header ${compact ? "new-layout-header--compact" : ""}`}
    >
      <Button
        className="new-layout-header-toggle"
        icon={sidebarCollapsed ? "sidebar" : "indent"}
        onClick={toggleSidebar}
        aria-label={sidebarCollapsed ? "Open menu" : "Collapse menu"}
      />
      <div className="new-layout-header-portal" id="header-portal">
        <Breadcrumbs />
      </div>
      <LoginNav
        session={session}
        lang={lang}
        setLang={setLang}
        compact={compact}
      />
    </header>
  );
};

const LoginNav = ({ session, lang, setLang, compact }) => {
  const dispatch = useDispatch();
  const { axiosInstance } = useContext(AxiosContext);

  const handleLanguageChange = (l) => {
    setLang(l);
    if (axiosInstance) {
      axiosInstance
        .post("/users/settings", { username: session.userName, language: l })
        .catch(() => {});
    }
  };

  const name = localStorage.getItem(
    `swobbee.portal.${awsconfig.REACT_APP_REST_CLIENT_ID}.name`,
  );

  if (!name) return null;

  return (
    <nav
      className={`new-layout-user-nav ${compact ? "new-layout-user-nav--compact" : ""}`}
    >
      <Dropdown
        icon={null}
        trigger={
          <span
            className="new-layout-user-trigger"
            data-testid="user-menu-trigger"
          >
            <Image
              size="mini"
              circular
              src={`https://eu.ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=ff5a00&color=fff&bold=true`}
              alt=""
            />
            {!compact && <span className="new-layout-user-name">{name}</span>}
            <Icon name="dropdown" />
          </span>
        }
        direction="left"
        className="new-layout-user-dropdown"
      >
        <Dropdown.Menu className="new-layout-user-menu" data-testid="user-menu">
          <Dropdown.Item
            as={Link}
            to="/profile"
            data-testid="user-menu-profile"
          >
            <Icon name="user" /> My profile
          </Dropdown.Item>
          {SUPPORTED_LANGUAGES.map((l) => (
            <Dropdown.Item
              key={l}
              active={lang === l}
              onClick={() => handleLanguageChange(l)}
              data-testid={`language-option-${l}`}
              aria-label={`Switch language to ${l}`}
            >
              <Flag name={l === "en" ? "us" : l} /> {l}
            </Dropdown.Item>
          ))}
          <Dropdown.Divider />
          <Dropdown.Item
            onClick={() => dispatch(logoutUser())}
            as={Link}
            to="/"
            data-testid="user-menu-logout"
          >
            <Icon name="sign out" /> Logout
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </nav>
  );
};

const SIDEBAR_BREAKPOINT = 768;
const HIDE_SIDEBAR_BREAKPOINT = 425;

const NewLayout = ({ session, children, setLang, lang, collapsed = true }) => {
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const sidebarCollapsed = useSelector(
    (state) => state.appSettings.sidebarIsCollapsed,
  );

  const closeSidebar = useCallback(
    () => dispatch(updateSideBarState(true)),
    [dispatch],
  );

  const showSidebar = width > HIDE_SIDEBAR_BREAKPOINT;
  const sidebarAsOverlay = width <= SIDEBAR_BREAKPOINT;

  return (
    <Container fluid className="screen-wrapper screen-wrapper--new-layout">
      {showSidebar && (
        <Sidebar
          collapsed={sidebarCollapsed}
          closeOnNavigate={sidebarAsOverlay}
        />
      )}
      <div
        className={`new-layout-body ${!showSidebar ? "new-layout-body--no-sidebar" : ""} ${sidebarAsOverlay ? "new-layout-body--sidebar-overlay" : ""}`}
      >
        <MainHeader
          session={session}
          lang={lang}
          setLang={setLang}
          collapsed={sidebarCollapsed}
          compact={width <= 576}
        />
        <main className="new-layout-main">
          <div
            className="new-layout-main-inner"
            style={{
              height: "90vh",
              minHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {children}
          </div>
        </main>
      </div>
      {showSidebar && !sidebarCollapsed && sidebarAsOverlay && (
        <div
          className="new-layout-overlay"
          onClick={closeSidebar}
          onKeyDown={(e) => e.key === "Escape" && closeSidebar()}
          role="button"
          tabIndex={0}
          aria-label="Close menu"
        />
      )}
    </Container>
  );
};

export { NewLayout };
